import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnalyticsEvent, EventFilters } from '../types/Analytics';
import { analyticsService } from '../services/analyticsService';
import { Clock, RefreshCcw, TrendingUp, User, Trash2, Loader2 } from 'lucide-react';
import { db } from '../config/firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

interface UserData {
    id: string;
    name: string;
}

interface EventsTableProps {
    days: number;
    refreshKey?: number;
}

const formatEventDetails = (event: AnalyticsEvent) => {
    const m = event.metadata || {};

    switch (event.eventType) {
        case 'level_complete':
            const level = m.levelNumber || m.levelId || 'Unknown';
            const time = m.timeSpent ? `${m.timeSpent}s` : '';
            const errors = m.wrongAnswers !== undefined ? `${m.wrongAnswers} errors` : '';
            const attempts = m.attempts !== undefined ? `${m.attempts} attempts` : '';
            const success = m.success !== undefined ? (m.success ? '✓' : '✗') : '';

            const parts = [time, errors || attempts, success].filter(Boolean);
            return `Level ${level}${parts.length ? ' - ' + parts.join(', ') : ''}`;
        case 'hint_used':
            const source = m.source ? ` (Used ${m.source})` : '';
            return `${m.hintType || 'Unknown'} hint${source}${m.hintsRemaining !== undefined ? ` [${m.hintsRemaining} left]` : ''}`;
        case 'ad_watched':
            return `${m.placement || 'Ad'}${m.rewardAmount ? ` - Earned ${m.rewardAmount} ${m.rewardType || 'coins'}` : ''}`;
        case 'iap_purchase':
            return `${m.itemName || 'Item'}${m.priceUSD ? ` ($${m.priceUSD.toFixed(2)})` : ''}`;
        case 'item_purchase':
            return `${m.itemName || 'Item'}${m.price ? ` (${m.price} ${m.currency || 'coins'})` : ''}`;
        case 'interstitial_ad':
            return `${m.placement || 'Interstitial'}${m.levelsSinceLastAd !== undefined ? ` (after ${m.levelsSinceLastAd} levels)` : ''}`;
        case 'store_review_click':
            return `From: ${m.source || 'Unknown'}`;
        case 'user_created':
            return `Welcome to ${m.userName || 'a new user'}!`;
        default:
            return JSON.stringify(m).length > 2 ? JSON.stringify(m) : 'No details';
    }
};

const EventsTable: React.FC<EventsTableProps> = ({ days, refreshKey }) => {
    const navigate = useNavigate();
    const [events, setEvents] = useState<AnalyticsEvent[]>([]);
    const [users, setUsers] = useState<Map<string, UserData>>(new Map());
    const [loading, setLoading] = useState(true);
    const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
    const [filters, setFilters] = useState<EventFilters>({});
    const [limit, setLimit] = useState(50);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isDeletingBulk, setIsDeletingBulk] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [usernameSearch, setUsernameSearch] = useState('');
    const [detailSearch, setDetailSearch] = useState('');

    const filteredEvents = React.useMemo(() => {
        return events.filter(event => {
            if (usernameSearch) {
                const user = users.get(event.userId);
                const username = user?.name || 'Unknown User';
                if (!username.toLowerCase().includes(usernameSearch.toLowerCase())) {
                    return false;
                }
            }
            if (detailSearch) {
                const details = formatEventDetails(event);
                if (!details.toLowerCase().includes(detailSearch.toLowerCase())) {
                    return false;
                }
            }
            return true;
        });
    }, [events, users, usernameSearch, detailSearch]);

    const paginatedEvents = React.useMemo(() => filteredEvents.slice(0, limit), [filteredEvents, limit]);
    const selectedShownIds = React.useMemo(() => Array.from(selectedIds).filter(id => paginatedEvents.some(e => e.id === id)), [selectedIds, paginatedEvents]);

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        const allShownSelected = paginatedEvents.length > 0 && selectedShownIds.length === paginatedEvents.length;
        if (allShownSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                paginatedEvents.forEach(e => next.delete(e.id));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                paginatedEvents.forEach(e => next.add(e.id));
                return next;
            });
        }
    };

    const handleBulkDelete = async () => {
        if (selectedShownIds.length === 0) return;

        // eslint-disable-next-line no-restricted-globals
        const confirmed = window.confirm(`Are you sure you want to delete ${selectedShownIds.length} selected events? This action cannot be undone.`);

        if (!confirmed) return;

        setIsDeletingBulk(true);
        try {
            const deletePromises = selectedShownIds.map(id => deleteDoc(doc(db, 'events', id)));
            await Promise.all(deletePromises);

            // Refresh local state
            setEvents(prev => prev.filter(e => !selectedShownIds.includes(e.id)));
            setSelectedIds(prev => {
                const next = new Set(prev);
                selectedShownIds.forEach(id => next.delete(id));
                return next;
            });
        } catch (error) {
            console.error('Bulk delete failed:', error);
            alert('Failed to delete some events. Page will refresh.');
            loadUsersAndEvents();
        } finally {
            setIsDeletingBulk(false);
        }
    };

    const loadUsersAndEvents = useCallback(async (isManual = false) => {
        if (isManual) setRefreshing(true);
        else setLoading(true);
        try {
            // Load users for name lookup
            const usersSnapshot = await getDocs(collection(db, 'Users'));
            const usersMap = new Map<string, UserData>();
            usersSnapshot.forEach((doc) => {
                const data = doc.data();
                usersMap.set(doc.id, {
                    id: doc.id,
                    name: data.name || 'Unknown User'
                });
            });
            setUsers(usersMap);

            // Build date-range filter based on selected days
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - days);
            const timeFilter = days >= 3650 ? {} : { startDate: cutoffDate };

            // Fetch up to 2000 recent events so client-side filtering has a large pool to search through
            const data = await analyticsService.getAllEvents(2000, { ...filters, ...timeFilter });
            setEvents(data);
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [filters, days]);

    useEffect(() => {
        loadUsersAndEvents();
    }, [loadUsersAndEvents, refreshKey]);

    const handleDeleteEvent = async (eventId: string) => {
        console.log('Delete requested for event:', eventId);
        // Using window.confirm and disabling lint to bypass restricted-globals issue
        // eslint-disable-next-line no-restricted-globals
        const confirmed = window.confirm('Are you sure you want to delete this event? This action cannot be undone.');

        if (!confirmed) {
            console.log('Delete cancelled');
            return;
        }

        try {
            console.log('Deleting document from Firestore:', eventId);
            await deleteDoc(doc(db, 'events', eventId));
            // Refresh events list
            setEvents(prev => prev.filter(e => e.id !== eventId));
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(eventId);
                return next;
            });
        } catch (error) {
            console.error('Failed to delete event:', error);
            alert('Failed to delete event. Please try again.');
        }
    };

    const getEventTypeBadge = (eventType: string) => {
        const styles: Record<string, React.CSSProperties> = {
            level_complete: { background: '#10b981', color: 'white' },
            hint_used: { background: '#3b82f6', color: 'white' },
            ad_watched: { background: '#f59e0b', color: 'white' },
            iap_purchase: { background: '#8b5cf6', color: 'white' },
            interstitial_ad: { background: '#fb923c', color: 'white' },
            store_review_click: { background: '#14b8a6', color: 'white' },
            item_purchase: { background: '#06b6d4', color: 'white' },
            user_created: { background: '#f43f5e', color: 'white' }
        };

        return (
            <span style={{
                ...styles[eventType] || { background: '#6b7280', color: 'white' },
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600',
                textTransform: 'capitalize'
            }}>
                {eventType.replace('_', ' ')}
            </span>
        );
    };

    const getEventSimpleName = (eventType: string) => {
        const names: Record<string, string> = {
            level_complete: 'Level completed',
            hint_used: 'Hint taken',
            ad_watched: 'Ad watched',
            iap_purchase: 'Real Money Purchase (IAP)',
            item_purchase: 'Virtual Purchase (Coins)',
            interstitial_ad: 'Interstitial shown',
            store_review_click: 'Store review clicked',
            user_created: 'New User joined'
        };
        return names[eventType] || eventType.replace('_', ' ');
    };



    if (loading) {
        return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading events...</div>;
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrendingUp size={20} color="#3b82f6" />
                    Recent Events
                </h3>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <button
                        onClick={() => loadUsersAndEvents(true)}
                        disabled={refreshing || loading}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            background: 'white',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            cursor: (refreshing || loading) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => !(refreshing || loading) && (e.currentTarget.style.borderColor = '#9ca3af')}
                        onMouseLeave={(e) => !(refreshing || loading) && (e.currentTarget.style.borderColor = '#d1d5db')}
                        title="Manual Refresh"
                    >
                        <RefreshCcw size={16} className={(refreshing || loading) ? "animate-spin" : ""} />
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                    {selectedShownIds.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            disabled={isDeletingBulk}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: isDeletingBulk ? 'not-allowed' : 'pointer',
                                transition: 'background 0.2s',
                                opacity: isDeletingBulk ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => !isDeletingBulk && (e.currentTarget.style.background = '#dc2626')}
                            onMouseLeave={(e) => !isDeletingBulk && (e.currentTarget.style.background = '#ef4444')}
                        >
                            {isDeletingBulk ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            Delete Selected ({selectedShownIds.length})
                        </button>
                    )}
                    <select
                        value={filters.eventType || ''}
                        onChange={(e) => setFilters({ ...filters, eventType: e.target.value as any || undefined })}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                        }}
                    >
                        <option value="">All Events</option>
                        <option value="level_complete">Level Complete</option>
                        <option value="hint_used">Hint Used</option>
                        <option value="ad_watched">Ad Watched</option>
                        <option value="iap_purchase">Real Money Purchase (IAP)</option>
                        <option value="item_purchase">Virtual Purchase (Coins)</option>
                        <option value="interstitial_ad">Interstitial Ad</option>
                        <option value="store_review_click">Store Review Click</option>
                        <option value="user_created">New User Created</option>
                    </select>
                    <input
                        type="text"
                        placeholder="Search username..."
                        value={usernameSearch}
                        onChange={(e) => setUsernameSearch(e.target.value)}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            minWidth: '150px'
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Search details..."
                        value={detailSearch}
                        onChange={(e) => setDetailSearch(e.target.value)}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            minWidth: '150px'
                        }}
                    />
                    <select
                        value={limit}
                        onChange={(e) => setLimit(Number(e.target.value))}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                        }}
                    >
                        <option value={25}>25 events</option>
                        <option value={50}>50 events</option>
                        <option value={100}>100 events</option>
                        <option value={250}>250 events</option>
                    </select>
                </div>
            </div>

            <div style={{
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                overflow: 'visible'
            }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <tr>
                            <th style={{ padding: '0.75rem', textAlign: 'center', width: '40px' }}>
                                <input
                                    type="checkbox"
                                    checked={paginatedEvents.length > 0 && selectedShownIds.length === paginatedEvents.length}
                                    onChange={toggleSelectAll}
                                    style={{ cursor: 'pointer' }}
                                />
                            </th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Event Type</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>User</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Event</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Time</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', width: '80px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            if (paginatedEvents.length === 0) {
                                return (
                                    <tr>
                                        <td colSpan={6} style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
                                            {usernameSearch || detailSearch ? 'No events match your search criteria' : 'No events found'}
                                        </td>
                                    </tr>
                                );
                            }

                            return paginatedEvents.map((event) => (
                                <tr key={event.id} style={{
                                    borderBottom: '1px solid #f3f4f6',
                                    background: selectedIds.has(event.id) ? '#fff7ed' : 'transparent'
                                }}>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(event.id)}
                                            onChange={() => toggleSelect(event.id)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </td>
                                    <td style={{ padding: '0.75rem' }}>
                                        {getEventTypeBadge(event.eventType)}
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#374151' }}>
                                        <button
                                            onClick={() => navigate(`/users?edit=${event.userId}`)}
                                            title={event.userId}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#3b82f6',
                                                textDecoration: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.25rem',
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '4px',
                                                transition: 'background 0.2s',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                        >
                                            <User size={14} />
                                            {users.get(event.userId)?.name || event.userId}
                                        </button>
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280', position: 'relative' }}>
                                        <div
                                            style={{ cursor: 'help' }}
                                            onMouseEnter={() => setHoveredEventId(event.id)}
                                            onMouseLeave={() => setHoveredEventId(null)}
                                        >
                                            {getEventSimpleName(event.eventType)}
                                            {hoveredEventId === event.id && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: '0',
                                                        zIndex: 9999,
                                                        background: '#1f2937',
                                                        color: '#f3f4f6',
                                                        padding: '0.75rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.75rem',
                                                        minWidth: '250px',
                                                        maxWidth: '400px',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
                                                        marginTop: '4px',
                                                        fontFamily: 'monospace',
                                                        pointerEvents: 'none'
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#3b82f6', borderBottom: '1px solid #4b5563', paddingBottom: '0.25rem' }}>
                                                        {formatEventDetails(event)}
                                                    </div>
                                                    <div style={{ opacity: 0.9 }}>
                                                        {JSON.stringify(event.metadata || {}, null, 2)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <Clock size={14} />
                                            {new Date(event.timestamp).toLocaleString()}
                                        </div>
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center', position: 'relative', zIndex: 50 }}>
                                        <button
                                            onClick={(e) => {
                                                console.log('Button clicked!');
                                                e.stopPropagation();
                                                handleDeleteEvent(event.id);
                                            }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#ef4444',
                                                padding: '0.5rem',
                                                borderRadius: '4px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                transition: 'background 0.2s',
                                                position: 'relative',
                                                zIndex: 100
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                            title="Delete event"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ));
                        })()}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EventsTable;
